import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import logo from '../../Header/Images/RMB-Strip-Jaipur.png';
import scanner from '../../stickySide/images/sacnner.jpg';
import Header from '../../Header/Header';
import Footer from '../../Footer/Footer';
import { FaUserCheck, FaShareAlt, FaCopy, FaCheckCircle, FaCalendarAlt, FaBuilding, FaUserFriends, FaQrcode } from 'react-icons/fa';

interface DynamicField {
  id: number;
  form_type: string;
  field_label: string;
  field_name: string;
  field_type: 'text' | 'email' | 'number' | 'tel' | 'date' | 'textarea' | 'select' | 'file' | 'qr_payment';
  options?: any;
  is_required: number;
  sort_order: number;
}

interface EventItem {
  id: string | number;
  heading?: string;
  title?: string;
  event_date: string;
  location?: string;
}

interface MemberItem {
  id: number;
  name: string;
  company_name?: string;
}

const VisitorRegisterPage: React.FC = () => {
  const { eventId: urlEventId } = useParams<{ eventId?: string }>();
  const [searchParams] = useSearchParams();
  const queryEventId = searchParams.get('eventId') || searchParams.get('event');
  const activeEventId = urlEventId || queryEventId || '';

  const [fields, setFields] = useState<DynamicField[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);

  const [selectedEventId, setSelectedEventId] = useState<string>(activeEventId);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Default fallback fields if no custom fields built in admin yet
  const defaultFields: DynamicField[] = [
    { id: 1, form_type: 'VISITOR_REGISTRATION', field_label: 'Visitor Full Name', field_name: 'full_name', field_type: 'text', is_required: 1, sort_order: 1 },
    { id: 2, form_type: 'VISITOR_REGISTRATION', field_label: 'Email Address', field_name: 'email', field_type: 'email', is_required: 1, sort_order: 2 },
    { id: 3, form_type: 'VISITOR_REGISTRATION', field_label: 'Mobile / WhatsApp Number', field_name: 'mobile', field_type: 'tel', is_required: 1, sort_order: 3 },
    { id: 4, form_type: 'VISITOR_REGISTRATION', field_label: 'Company / Firm Name', field_name: 'company_name', field_type: 'text', is_required: 1, sort_order: 4 },
    { id: 5, form_type: 'VISITOR_REGISTRATION', field_label: 'Business Category / Industry', field_name: 'classification', field_type: 'text', is_required: 1, sort_order: 5 },
    { id: 6, form_type: 'VISITOR_REGISTRATION', field_label: 'Invited By (RMB Member Name)', field_name: 'invited_by', field_type: 'select', is_required: 0, sort_order: 6 },
    { id: 7, form_type: 'VISITOR_REGISTRATION', field_label: 'Registration Fee (Rs. 600/- QR Payment)', field_name: 'payment_confirm', field_type: 'qr_payment', is_required: 1, sort_order: 7 }
  ];

  useEffect(() => {
    fetchDynamicFields();
    fetchUpcomingEvents();
    fetchMembers();
  }, []);

  useEffect(() => {
    if (activeEventId) {
      setSelectedEventId(activeEventId);
    }
  }, [activeEventId]);

  useEffect(() => {
    if (selectedEventId && events.length > 0) {
      const match = events.find(e => String(e.id) === String(selectedEventId));
      if (match) setSelectedEvent(match);
    }
  }, [selectedEventId, events]);

  const fetchDynamicFields = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields?form_type=VISITOR_REGISTRATION');
      if (res.data.success && Array.isArray(res.data.fields) && res.data.fields.length > 0) {
        setFields(res.data.fields);
      } else {
        setFields(defaultFields);
      }
    } catch (err) {
      console.error('Error fetching visitor fields:', err);
      setFields(defaultFields);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/events');
      const allEvents: EventItem[] = res.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const upcoming = allEvents.filter((e: any) => {
        if (!e.event_date) return true;
        const d = new Date(e.event_date);
        return d >= today;
      });

      setEvents(upcoming.length > 0 ? upcoming : allEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
      if (Array.isArray(res.data)) {
        setMembers(res.data.map((u: any) => ({ id: u.id, name: u.name, company_name: u.company_name })));
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const handleCopyLink = () => {
    const shareableUrl = `${window.location.origin}/visitor-register${selectedEventId ? `?eventId=${selectedEventId}` : ''}`;
    navigator.clipboard.writeText(shareableUrl);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 3000);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg('');

    // Extract name, mobile, and email flexibly from any key in formData
    const keys = Object.keys(formData);
    const nameKey = keys.find(k => k.includes('name')) || 'full_name';
    const mobileKey = keys.find(k => k.includes('mobile') || k.includes('phone') || k.includes('number') || k.includes('contact') || k.includes('tel')) || 'mobile';
    const emailKey = keys.find(k => k.includes('email') || k.includes('mail')) || 'email';

    const visitorName = formData[nameKey] || formData.full_name || formData.visitor_name || formData.name || (keys.length > 0 ? formData[keys[0]] : '');
    const visitorMobile = formData[mobileKey] || formData.mobile || formData.visitor_phone || formData.phone || formData.number || '';
    const visitorEmail = formData[emailKey] || formData.email || formData.visitor_email || '';

    if (!visitorName || (!visitorMobile && !visitorEmail)) {
      setErrMsg('Please fill out your Full Name and Mobile Number or Email Address.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        full_name: visitorName,
        mobile: visitorMobile,
        email: visitorEmail,
        event_id: selectedEventId || null,
        event_name: selectedEvent?.heading || selectedEvent?.title || 'General Chapter Meeting',
        event_date: selectedEvent?.event_date || ''
      };

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submit', {
        form_type: 'VISITOR_REGISTRATION',
        data: payload
      });

      if (res.data.success) {
        setSubmittedSuccess(true);
      } else {
        setErrMsg(res.data.error || res.data.message || 'Failed to submit registration.');
      }
    } catch (err: any) {
      console.error('Error submitting visitor registration:', err);
      const exactErr = err.response?.data?.details || err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to submit registration.';
      setErrMsg(exactErr);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: DynamicField) => {
    const value = formData[field.field_name] || '';

    if (field.field_type === 'qr_payment') {
      let qrImg = scanner;
      let amountStr = '600';
      try {
        if (field.options) {
          const parsed = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
          if (parsed.qr_image) qrImg = parsed.qr_image;
          if (parsed.amount) amountStr = parsed.amount;
        }
      } catch {}

      return (
        <div key={field.id} className="bg-[#1E2B45] p-5 rounded-2xl border border-amber-500/40 shadow-md text-left">
          <label className="block text-xs font-black text-amber-300 uppercase tracking-wider mb-2">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <div className="flex flex-col sm:flex-row items-center gap-5 bg-[#131E32] p-4 rounded-xl border border-[#2A3B5C]">
            <img src={qrImg} alt="RMB Visitor Payment QR Code Scanner" className="w-36 h-36 object-contain rounded-xl border border-slate-700 p-1.5 bg-white shadow-md flex-shrink-0" />
            <div className="text-xs text-slate-300 space-y-2 flex-1">
              <p className="font-black text-white text-sm">Scan QR Code using GPay / PhonePe / Paytm / UPI</p>
              <p className="text-slate-200 font-bold">
                Visitor Fee: <span className="text-amber-400 font-black text-base">₹{amountStr}/-</span> per attendee
              </p>
              <p className="text-[11px] text-slate-400">Includes Meeting Entrance, High Tea & Networking Opportunities.</p>
              
              <label className="flex items-center gap-2.5 mt-3 font-bold text-slate-200 bg-[#0A2451] p-3 rounded-xl border border-[#2A3B5C] cursor-pointer hover:border-amber-400 transition">
                <input
                  type="checkbox"
                  required={Boolean(field.is_required)}
                  checked={Boolean(formData[field.field_name])}
                  onChange={(e) => handleFieldChange(field.field_name, e.target.checked)}
                  className="accent-[#FDBC12] w-4 h-4"
                />
                I have completed the QR payment of ₹{amountStr}/-
              </label>
            </div>
          </div>
        </div>
      );
    }

    if (field.field_name === 'invited_by' || field.field_type === 'select') {
      let optionsList: string[] = [];
      if (field.field_name === 'invited_by') {
        optionsList = members.map(m => m.name);
      } else {
        try {
          if (field.options) optionsList = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
        } catch {
          optionsList = field.options ? (field.options as string).split(',') : [];
        }
      }

      return (
        <div key={field.id} className="text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <select
            required={Boolean(field.is_required)}
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12] cursor-pointer"
          >
            <option value="">-- Select {field.field_label} --</option>
            {optionsList.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.field_type === 'textarea') {
      return (
        <div key={field.id} className="text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <textarea
            required={Boolean(field.is_required)}
            value={value}
            rows={3}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            placeholder={`Enter ${field.field_label}`}
            className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
          />
        </div>
      );
    }

    if (field.field_type === 'file') {
      return (
        <div key={field.id} className="text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <input
            type="file"
            accept="image/*,.pdf"
            required={Boolean(field.is_required)}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => handleFieldChange(field.field_name, reader.result);
                reader.readAsDataURL(file);
              }
            }}
            className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-xs text-slate-300"
          />
        </div>
      );
    }

    return (
      <div key={field.id} className="text-left">
        <label className="block text-xs font-bold text-slate-200 mb-1">
          {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
        </label>
        <input
          type={field.field_type === 'number' || field.field_type === 'tel' ? 'text' : field.field_type}
          required={Boolean(field.is_required)}
          value={value}
          onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
          placeholder={`Enter ${field.field_label}`}
          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 font-sans">
      <Header />

      <main className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Shareable Link Top Bar */}
        <div className="bg-gradient-to-r from-[#0A2451] to-[#1E2B45] p-4 rounded-2xl border border-cyan-500/30 shadow-xl mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FDBC12]/10 border border-[#FDBC12]/30 flex items-center justify-center text-[#FDBC12]">
              <FaShareAlt size={18} />
            </div>
            <div>
              <h3 className="font-extrabold text-white text-sm">Share Visitor Registration Link</h3>
              <p className="text-xs text-slate-300">Invite guest business owners to attend RMB Jaipur chapter meetings.</p>
            </div>
          </div>
          <button
            onClick={handleCopyLink}
            className="px-5 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <FaCopy size={13} /> {copyFeedback ? '✓ Link Copied!' : 'Copy Registration Link'}
          </button>
        </div>

        {/* Main Form Container */}
        <div className="bg-[#131E32] rounded-3xl border border-[#2A3B5C] shadow-2xl overflow-hidden">
          {/* Form Header Banner */}
          <div className="bg-gradient-to-r from-[#0A2451] via-[#02256D] to-[#1E2B45] p-8 text-center border-b border-[#2A3B5C] space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-[#FDBC12] text-[#0A2451] font-black text-xs rounded-full uppercase tracking-wider shadow">
              🎟️ Visitor Registration Form
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Join RMB Jaipur Chapter Meeting</h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
              Fill out the visitor meeting registration form below to participate in our upcoming networking meeting, connect with top Jaipur business leaders, and present your firm.
            </p>
          </div>

          <div className="p-6 sm:p-8">
            {submittedSuccess ? (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 bg-emerald-500/20 border border-emerald-500/40 rounded-full flex items-center justify-center mx-auto text-emerald-400 text-3xl">
                  <FaCheckCircle />
                </div>
                <h2 className="text-2xl font-black text-white">Visitor Registration Submitted Successfully! 🎉</h2>
                <p className="text-sm text-slate-300 max-w-md mx-auto">
                  Thank you for registering as a visitor for RMB Jaipur. Your registration has been received and sent to the Admin team for verification.
                </p>
                <div className="pt-4">
                  <button
                    onClick={() => { setSubmittedSuccess(false); setFormData({}); }}
                    className="px-6 py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg cursor-pointer transition"
                  >
                    Register Another Visitor
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {errMsg && (
                  <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs font-bold">
                    ⚠️ {errMsg}
                  </div>
                )}

                {/* Upcoming Event Selection */}
                <div className="bg-[#0A2451] p-5 rounded-2xl border border-cyan-500/40 space-y-3">
                  <label className="block text-xs font-black text-amber-300 uppercase tracking-wider">
                    📅 Select Meeting / Event to Attend
                  </label>
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full p-3.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-extrabold text-sm outline-none focus:border-[#FDBC12] cursor-pointer"
                  >
                    <option value="">-- Select Upcoming Chapter Event / Meeting --</option>
                    {events.map(evt => (
                      <option key={evt.id} value={String(evt.id)}>
                        {evt.heading || evt.title || 'RMB Chapter Meeting'} ({evt.event_date ? new Date(evt.event_date).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : 'Upcoming'})
                      </option>
                    ))}
                  </select>

                  {selectedEvent && (
                    <div className="p-3 bg-[#131E32] rounded-xl border border-amber-500/30 text-xs text-slate-200 flex flex-col sm:flex-row justify-between gap-2">
                      <span className="font-extrabold text-white">Event: {selectedEvent.heading || selectedEvent.title}</span>
                      <span className="text-[#FDBC12] font-bold">Date: {selectedEvent.event_date ? new Date(selectedEvent.event_date).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' }) : 'Upcoming'}</span>
                    </div>
                  )}
                </div>

                {/* Render Dynamic Custom Fields from Admin Builder */}
                <div className="space-y-4">
                  <div className="border-b border-[#2A3B5C] pb-2 text-left">
                    <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                      <FaUserCheck className="text-[#FDBC12]" /> Visitor Information
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {fields.map(field => renderField(field))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-base rounded-2xl shadow-2xl transition cursor-pointer disabled:opacity-50 mt-6"
                >
                  {submitting ? 'Submitting Visitor Registration...' : '🚀 Submit Visitor Registration'}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VisitorRegisterPage;
